import axios from 'axios';
import jwtDecode from 'jwt-decode';
import {
  auth0TokenApiParams,
  REFRESH_TOKEN_COOKIE,
  providerTokenEndpoint,
} from '../config';
import { persistApiRegion } from '../utils/apiRegion';
import cookieService from './cookieService';
import { logger } from '../logger';

const log = logger();

const REGION_REGEX = /\.api\.(eu|gl)\./;

const getRegionFromUrl = (url) => {
  const matchedRegion = url?.match(REGION_REGEX)?.[1];
  return matchedRegion || null;
};

const swapRegionInUrl = (url, region) => {
  if (!url || !region) {
    return url;
  }
  return url.replace(REGION_REGEX, `.api.${region}.`);
};

const getAlternateRegion = (region) => {
  if (region === 'eu') {
    return 'gl';
  }
  if (region === 'gl') {
    return 'eu';
  }
  return null;
};

const convertAuthorisationCodeIntoTokens = async (code) => {
  let response;
  const primaryUrl = `${auth0TokenApiParams.url}`;
  const primaryRegion = getRegionFromUrl(primaryUrl);

  const data = {
    ...auth0TokenApiParams.data,
    code,
    redirect_uri: btoa(auth0TokenApiParams.data.redirect_uri),
  };

  try {
    response = await axios.post(primaryUrl, data);
    if (primaryRegion) {
      persistApiRegion(primaryRegion);
    }
  } catch (err) {
    const alternateRegion = getAlternateRegion(primaryRegion);
    const alternateUrl = swapRegionInUrl(primaryUrl, alternateRegion);

    if (
      !alternateRegion ||
      !alternateUrl ||
      alternateUrl === primaryUrl
    ) {
      log.error(
        'Error converting authorisation code into tokens',
        err,
      );
      return {
        ...{
          accessToken: null,
          idToken: null,
          refreshToken: null,
          expiresIn: null,
        },
      };
    }

    try {
      response = await axios.post(alternateUrl, data);
      persistApiRegion(alternateRegion);
    } catch (fallbackErr) {
      log.error('Error converting authorisation code into tokens', {
        primaryError: err,
        fallbackError: fallbackErr,
      });
    }
  }

  return {
    ...{
      accessToken: response?.data?.access_token ?? null,
      idToken: response?.data?.id_token ?? null,
      refreshToken: response?.data?.refresh_token ?? null,
      expiresIn: response?.data?.expires_in ?? null,
    },
  };
};

const getProviderToken = async (clinicId) => {
  const refreshToken = cookieService.getCookie(REFRESH_TOKEN_COOKIE);
  const postData = {
    grant_type: 'urn:ietf:params:oauth:grant-type:token-exchange',
    providerId: clinicId,
    refresh_token: refreshToken,
  };
  const config = {
    headers: {
      'Content-Type': 'application/json',
    },
  };

  try {
    const response = await axios.post(
      providerTokenEndpoint,
      postData,
      config,
    );
    return {
      accessToken: response.data.access_token,
      refreshToken: response.data.refresh_token,
      expiresIn: response?.data?.expires_in ?? null,
    };
  } catch (err) {
    log.error('Error getting provider token', err);
  }
  return false;
};

const getTokenExpiry = (token) => {
  try {
    const decoded = jwtDecode(token);
    return decoded.exp;
  } catch (err) {
    log.error('Error getting token expiry', err);
    return -1;
  }
};

const tokenService = {
  convertAuthorisationCodeIntoTokens,
  getProviderToken,
  getTokenExpiry,
};
export default tokenService;
